import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Search,
  Filter,
  Eye,
  Loader2,
  DollarSign,
  Calendar,
  Package,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Building,
  Clock,
  Tag,
  FileText,
  Globe,
  Hash,
  Video,
  Users,
  Play,
  Download,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import InvoicePreview from '@/components/invoice/InvoicePreview';

const ITEMS_PER_PAGE = 50;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('webinars');
  
  // Speaker data cache for hover cards
  const [speakerCache, setSpeakerCache] = useState({});

  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Separate counts
  const [webinarOrderCount, setWebinarOrderCount] = useState(0);
  const [seminarOrderCount, setSeminarOrderCount] = useState(0);

  useEffect(() => {
    fetchOrders();
  }, [page, statusFilter, activeTab]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = { 
        page, 
        per_page: ITEMS_PER_PAGE,
        order_type: activeTab === 'webinars' ? 'WEB' : 'SEM'
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await adminAPI.getOrders(params);
      setOrders(response.data.orders || response.data || []);
      setTotalOrders(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / ITEMS_PER_PAGE));
      
      // Set counts
      if (response.data.webinar_count !== undefined) {
        setWebinarOrderCount(response.data.webinar_count);
      }
      if (response.data.seminar_count !== undefined) {
        setSeminarOrderCount(response.data.seminar_count);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch speaker data for hover cards
  const fetchSpeakerData = async (webinarId) => {
    if (!webinarId || speakerCache[webinarId]) return;
    
    try {
      const response = await fetch(`${API_URL}/api/webinars/${webinarId}`);
      if (response.ok) {
        const webinar = await response.json();
        if (webinar.speakers && webinar.speakers.length > 0) {
          setSpeakerCache(prev => ({
            ...prev,
            [webinarId]: webinar.speakers[0]
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching speaker:', error);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailDialog(true);
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await adminAPI.updateOrderStatus(orderId, status);
      toast.success('Order status updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status });
      }
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  // Helper to get customer name
  const getCustomerName = (order) => {
    if (order.customer_name) return order.customer_name;
    if (order.billing_first_name && order.billing_last_name) {
      return `${order.billing_first_name} ${order.billing_last_name}`;
    }
    if (order.billing_address?.first_name && order.billing_address?.last_name) {
      return `${order.billing_address.first_name} ${order.billing_address.last_name}`;
    }
    return order.billing_name || 'N/A';
  };

  // Helper to get customer email
  const getCustomerEmail = (order) => {
    return order.customer_email || order.billing_email || '';
  };

  // Helper to get customer phone
  const getCustomerPhone = (order) => {
    return order.customer_phone || order.billing_phone || '';
  };

  // Helper to format address
  const formatAddress = (order, type = 'billing') => {
    if (type === 'billing') {
      if (order.billing_address && typeof order.billing_address === 'object') {
        const addr = order.billing_address;
        return {
          company: addr.company || '',
          line1: addr.address || '',
          line2: [addr.city, addr.state || addr.zone, addr.postal_code || addr.postcode].filter(Boolean).join(', '),
          country: addr.country || ''
        };
      }
      return {
        company: order.billing_company || '',
        line1: order.billing_address || '',
        line2: [order.billing_city, order.billing_state, order.billing_zip].filter(Boolean).join(', '),
        country: order.billing_country || ''
      };
    }
    return {
      company: '',
      line1: order.shipping_address || '',
      line2: [order.shipping_city, order.shipping_state, order.shipping_zip].filter(Boolean).join(', '),
      country: order.shipping_country || ''
    };
  };

  // Helper to get item details
  const getItemDetails = (item) => ({
    title: item.webinar_title || item.title || 'Unknown Item',
    code: item.webinar_code || item.code || item.model || '',
    legacyCode: item.code || item.model || '',  // Keep legacy code separately
    variant: item.variant || item.access_type || item.item_type || 'Live Access',
    quantity: item.quantity || 1,
    unitPrice: item.unit_price || item.price || 0,
    totalPrice: item.total_price || item.total || item.price || 0,
    productId: item.opencart_product_id || item.item_id || item.webinar_id || '',
    webinarId: item.webinar_id || item.item_id || ''
  });

  // Helper to get all item codes for display
  const getItemCodes = (order) => {
    if (!order.items || order.items.length === 0) return [];
    return order.items.map(item => ({
      code: item.webinar_code || item.code || item.model || '',
      title: item.webinar_title || item.title || 'Unknown',
      variant: item.variant || item.access_type || 'Live Access',
      webinarId: item.webinar_id || item.item_id || ''
    }));
  };

  // Transform order data for InvoicePreview component
  const transformOrderForPreview = (order) => {
    if (!order) return null;
    const total = order.calculated_total || order.total || order.subtotal || 0;
    return {
      order_number: order.order_number || order.legacy_order_number,
      created_at: order.created_at,
      status: order.status,
      payment_status: order.status === 'completed' ? 'paid' : 'pending',
      payment_method: order.payment_method || 'Credit Card',
      customer_name: order.customer_name || `${order.billing_first_name || ''} ${order.billing_last_name || ''}`.trim(),
      billing_email: order.billing_email || order.customer_email,
      billing_phone: order.billing_phone,
      billing_company: order.billing_company,
      billing_address: order.billing_address,
      billing_city: order.billing_city,
      billing_state: order.billing_state,
      billing_zip: order.billing_zip,
      billing_country: order.billing_country,
      items: (order.items || []).map(item => ({
        webinar_code: item.webinar_code || item.model,
        webinar_title: item.webinar_title || item.title || item.name,
        variant: item.variant || item.variant_normalized || 'Webinar Access',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || item.price || 0,
        total_price: item.total_price || item.total || item.unit_price || item.price || 0,
        scheduled_date: item.scheduled_date,
        speaker: item.speaker
      })),
      subtotal: order.subtotal || total,
      discount_amount: order.discount_amount || 0,
      tax_amount: order.tax_amount || 0,
      total_amount: total
    };
  };

  // Download invoice using client-side InvoicePreview (same format as Invoice Management page)
  const handleDownloadInvoice = async (order) => {
    try {
      toast.info('Generating invoice PDF...');
      // Fetch full order details
      const response = await adminAPI.getInvoiceOrderDetails(order.id);
      const orderData = transformOrderForPreview(response.data);

      // Create a temporary container for the invoice preview
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      document.body.appendChild(container);

      // Import ReactDOM and render the preview
      const ReactDOM = await import('react-dom/client');
      const root = ReactDOM.createRoot(container);

      const InvoiceWrapper = () => (
        <div id="pdf-render-target">
          <InvoicePreview order={orderData} hideActions={true} />
        </div>
      );

      root.render(<InvoiceWrapper />);

      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get the invoice content div
      const invoiceContent = container.querySelector('#invoice-content');
      if (!invoiceContent) {
        throw new Error('Invoice content not found');
      }

      // Use html2pdf to generate PDF
      const html2pdf = (await import('html2pdf.js')).default;

      const invoiceNumber = `INV-${order.order_number?.replace('GRC-WEB-', '').replace('GRC-SEM-', '').replace('OC-', '') || 'ORDER'}`;

      const opt = {
        margin: 0,
        filename: `${invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(invoiceContent).save();

      // Cleanup
      root.unmount();
      document.body.removeChild(container);

      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Unable to download invoice');
    }
  };

  // Helper to get payment info
  const getPaymentInfo = (order) => ({
    method: order.payment_method || 'N/A',
    code: order.payment_code || '',
    status: order.payment_status || order.status || 'pending',
    stripeId: order.stripe_payment_id || order.stripe_charge_id || '',
    currency: order.currency || order.currency_code || 'USD'
  });

  // Helper to get order total
  const getOrderTotal = (order) => {
    return order.total || order.total_amount || 0;
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    const customerName = getCustomerName(order);
    const customerEmail = getCustomerEmail(order);
    const codes = getItemCodes(order).map(c => c.code).join(' ');
    return (
      order.order_number?.toLowerCase().includes(searchLower) ||
      order.legacy_order_number?.toLowerCase().includes(searchLower) ||
      customerEmail.toLowerCase().includes(searchLower) ||
      customerName.toLowerCase().includes(searchLower) ||
      codes.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'payment_pending': return 'bg-orange-100 text-orange-700';
      case 'refunded': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'processing': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'refunded': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getVariantBadgeColor = (variant) => {
    const v = variant?.toLowerCase() || '';
    // Handle OpenCart variant names
    if (v.includes('live') && v.includes('download')) return 'bg-purple-100 text-purple-700';
    if (v.includes('live') && v.includes('usb')) return 'bg-indigo-100 text-indigo-700';
    if (v.includes('live') && v.includes('cd')) return 'bg-indigo-100 text-indigo-700';
    if (v.includes('group')) return 'bg-amber-100 text-amber-700';
    if (v.includes('live')) return 'bg-green-100 text-green-700';
    if (v.includes('download') || v.includes('recorded')) return 'bg-blue-100 text-blue-700';
    if (v.includes('usb') || v.includes('flash')) return 'bg-orange-100 text-orange-700';
    if (v.includes('cd') || v.includes('dvd')) return 'bg-pink-100 text-pink-700';
    return 'bg-slate-100 text-slate-700';
  };

  // Order Items Hover Card Component
  const OrderItemsHoverCard = ({ order }) => {
    const items = getItemCodes(order);
    
    if (items.length === 0) {
      return <span className="text-slate-400">No items</span>;
    }

    // Display title instead of code for better readability
    const displayText = items.length === 1 
      ? items[0].title.substring(0, 40) + (items[0].title.length > 40 ? '...' : '')
      : `${items[0].title.substring(0, 30)}... +${items.length - 1}`;

    return (
      <TooltipProvider>
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <button className="text-left hover:bg-slate-100 px-2 py-1 rounded transition-colors">
              <span className="text-sm font-medium text-navy-700">
                {displayText}
              </span>
              <span className="text-xs text-slate-500 ml-1">({items.length})</span>
            </button>
          </HoverCardTrigger>
          <HoverCardContent className="w-96 p-0" side="right" align="start">
            <div className="bg-navy-700 text-white px-4 py-3 rounded-t-md">
              <h4 className="font-semibold flex items-center gap-2">
                <Package className="w-4 h-4" />
                Order Items ({items.length})
              </h4>
            </div>
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {items.map((item, idx) => (
                <OrderItemPreview key={idx} item={item} onHover={() => fetchSpeakerData(item.webinarId)} speaker={speakerCache[item.webinarId]} />
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      </TooltipProvider>
    );
  };

  // Individual Order Item Preview
  const OrderItemPreview = ({ item, onHover, speaker }) => {
    useEffect(() => {
      onHover();
    }, []);

    return (
      <div className="p-3 hover:bg-slate-50 transition-colors">
        <div className="flex gap-3">
          {/* Speaker Image */}
          <div className="shrink-0">
            {speaker?.profile_image ? (
              <img 
                src={speaker.profile_image.startsWith('http') ? speaker.profile_image : `${API_URL}${speaker.profile_image}`}
                alt={speaker.name}
                className="w-14 h-14 rounded-lg object-cover border border-slate-200"
                onError={(e) => { e.target.src = `${API_URL}/static/images/default-avatar.png`; }}
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-200 flex items-center justify-center">
                <User className="w-6 h-6 text-slate-400" />
              </div>
            )}
          </div>
          
          {/* Item Details */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-800 text-sm line-clamp-2">{item.title}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">
                {item.code}
              </Badge>
              <Badge className={`${getVariantBadgeColor(item.variant)} text-xs capitalize`}>
                {item.variant}
              </Badge>
            </div>
            {speaker && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <User className="w-3 h-3" />
                {speaker.name || `${speaker.first_name} ${speaker.last_name}`}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Orders Table Component
  const OrdersTable = () => (
    <Card className="border-slate-200 rounded-xl overflow-hidden">
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Order ID</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Order Items</TableHead>
                  <TableHead className="font-semibold">Total</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No {activeTab === 'webinars' ? 'webinar' : 'seminar'} orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-slate-50" data-testid={`order-row-${order.order_number}`}>
                      <TableCell className="font-mono text-xs text-navy-700">
                        {order.order_number}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">
                            {getCustomerName(order)}
                          </p>
                          <p className="text-xs text-slate-500">{getCustomerEmail(order)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <OrderItemsHoverCard order={order} />
                      </TableCell>
                      <TableCell className="font-semibold text-slate-800">
                        ${getOrderTotal(order).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadgeColor(order.status)} capitalize text-xs`}>
                          {order.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs">
                        {order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewOrder(order)}
                            className="h-8 w-8"
                            title="View Details"
                            data-testid={`view-order-${order.order_number}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadInvoice(order)}
                              className="h-8 w-8 text-green-600 hover:text-green-700"
                              title="Download Invoice"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Order Management</h2>
          <p className="text-slate-500">
            Manage webinar and seminar orders
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="webinars" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Webinars
            <Badge variant="secondary" className="ml-1">{webinarOrderCount || totalOrders}</Badge>
          </TabsTrigger>
          <TabsTrigger value="seminars" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Seminars
            <Badge variant="secondary" className="ml-1">{seminarOrderCount || 0}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="webinars" className="mt-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Orders</p>
                    <p className="text-xl font-bold text-slate-800">{totalOrders.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Page Revenue</p>
                    <p className="text-xl font-bold text-slate-800">
                      ${orders.reduce((sum, o) => sum + getOrderTotal(o), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-xl font-bold text-slate-800">
                      {orders.filter(o => o.status === 'pending' || o.status === 'payment_pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completed</p>
                    <p className="text-xl font-bold text-slate-800">
                      {orders.filter(o => o.status === 'completed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Search by order ID, email, customer, or webinar code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-lg"
                    data-testid="order-search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-48 rounded-lg" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="payment_pending">Payment Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <OrdersTable />

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {Math.min(((page - 1) * ITEMS_PER_PAGE) + 1, totalOrders)} - {Math.min(page * ITEMS_PER_PAGE, totalOrders)} of {totalOrders.toLocaleString()} webinar orders
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="rounded-lg"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 px-2">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                Last
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seminars" className="mt-6 space-y-6">
          {/* Similar content for seminars */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Seminar Orders</p>
                    <p className="text-xl font-bold text-slate-800">{totalOrders.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Page Revenue</p>
                    <p className="text-xl font-bold text-slate-800">
                      ${orders.reduce((sum, o) => sum + getOrderTotal(o), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-xl font-bold text-slate-800">
                      {orders.filter(o => o.status === 'pending' || o.status === 'payment_pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completed</p>
                    <p className="text-xl font-bold text-slate-800">
                      {orders.filter(o => o.status === 'completed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Search by order ID, email, customer, or seminar code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-lg"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-48 rounded-lg">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <OrdersTable />

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {Math.min(((page - 1) * ITEMS_PER_PAGE) + 1, totalOrders)} - {Math.min(page * ITEMS_PER_PAGE, totalOrders)} of {totalOrders.toLocaleString()} seminar orders
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="rounded-lg"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 px-2">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="rounded-lg"
              >
                Last
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Enhanced Order Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
              <div className="space-y-6 py-4">
                {/* Order Header */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">New Order ID</p>
                    <p className="font-mono font-bold text-xl text-navy-700">{selectedOrder.order_number}</p>
                    {selectedOrder.legacy_order_number && (
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="font-medium">Legacy ID:</span> {selectedOrder.legacy_order_number}
                      </p>
                    )}
                    <p className="text-sm text-slate-500 mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {selectedOrder.created_at ? format(new Date(selectedOrder.created_at), 'MMMM dd, yyyy • h:mm a') : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={`${getStatusBadgeColor(selectedOrder.status)} capitalize text-sm px-3 py-1`}>
                      {selectedOrder.status?.replace('_', ' ')}
                    </Badge>
                    {getPaymentInfo(selectedOrder).status !== selectedOrder.status && (
                      <Badge className={`${getPaymentStatusColor(getPaymentInfo(selectedOrder).status)} capitalize text-xs px-2 py-0.5 ml-2`}>
                        {getPaymentInfo(selectedOrder).status}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Customer Information Section */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Customer Information
                    </h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Full Name</p>
                        <p className="font-medium text-slate-800">{getCustomerName(selectedOrder)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </p>
                        <p className="text-slate-700">{getCustomerEmail(selectedOrder) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Phone
                        </p>
                        <p className="text-slate-700">{getCustomerPhone(selectedOrder) || 'N/A'}</p>
                      </div>
                      {(selectedOrder.billing_company || formatAddress(selectedOrder).company) && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <Building className="w-3 h-3" /> Company
                          </p>
                          <p className="text-slate-700">{selectedOrder.billing_company || formatAddress(selectedOrder).company}</p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Billing Address
                        </p>
                        <div className="text-slate-700 text-sm space-y-0.5">
                          {formatAddress(selectedOrder).line1 && <p>{formatAddress(selectedOrder).line1}</p>}
                          {formatAddress(selectedOrder).line2 && <p>{formatAddress(selectedOrder).line2}</p>}
                          {formatAddress(selectedOrder).country && <p>{formatAddress(selectedOrder).country}</p>}
                          {!formatAddress(selectedOrder).line1 && !formatAddress(selectedOrder).line2 && <p className="text-slate-400">No address provided</p>}
                        </div>
                      </div>
                      {selectedOrder.ip_address && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <Globe className="w-3 h-3" /> IP Address
                          </p>
                          <p className="text-slate-700 font-mono text-sm">{selectedOrder.ip_address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Order Items Section */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Order Items ({selectedOrder.items?.length || 0})
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {selectedOrder.items?.map((item, idx) => {
                      const details = getItemDetails(item);
                      const speaker = speakerCache[details.webinarId];
                      return (
                        <div key={idx} className="p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex gap-4">
                            {/* Speaker Image */}
                            <div className="shrink-0">
                              {speaker?.profile_image ? (
                                <img 
                                  src={speaker.profile_image.startsWith('http') ? speaker.profile_image : `${API_URL}${speaker.profile_image}`}
                                  alt={speaker.name}
                                  className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                                  onError={(e) => { e.target.src = `${API_URL}/static/images/default-avatar.png`; }}
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center">
                                  <User className="w-6 h-6 text-slate-400" />
                                </div>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800">{details.title}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {details.code && (
                                  <Badge variant="outline" className="text-xs font-mono">
                                    <Hash className="w-3 h-3 mr-1" />
                                    {details.code}
                                  </Badge>
                                )}
                                <Badge className={`${getVariantBadgeColor(details.variant)} text-xs capitalize`}>
                                  {details.variant}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  Qty: {details.quantity}
                                </Badge>
                              </div>
                              {speaker && (
                                <p className="text-sm text-slate-600 mt-2 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {speaker.name || `${speaker.first_name} ${speaker.last_name}`}
                                </p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs text-slate-500">Unit Price</p>
                              <p className="text-sm text-slate-600">${details.unitPrice.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-2">Total</p>
                              <p className="font-semibold text-slate-800">${details.totalPrice.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    }) || (
                      <div className="p-4 text-center text-slate-500">
                        No items in this order
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Information Section */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Payment Information
                    </h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Payment Method</p>
                        <p className="font-medium text-slate-800">{getPaymentInfo(selectedOrder).method}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Payment Status</p>
                        <Badge className={`${getPaymentStatusColor(getPaymentInfo(selectedOrder).status)} capitalize`}>
                          {getPaymentInfo(selectedOrder).status}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {getPaymentInfo(selectedOrder).stripeId && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Stripe Charge ID</p>
                          <p className="font-mono text-sm text-slate-700 break-all">{getPaymentInfo(selectedOrder).stripeId}</p>
                        </div>
                      )}
                      {selectedOrder.promo_code && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Promo Code Applied
                          </p>
                          <Badge variant="outline" className="font-mono">{selectedOrder.promo_code}</Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Order Summary Section */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Order Summary
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-700">${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {(selectedOrder.discount_amount > 0 || selectedOrder.discount > 0) && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span>-${(selectedOrder.discount_amount || selectedOrder.discount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span className="text-slate-700">Total</span>
                      <span className="text-navy-700">${getOrderTotal(selectedOrder).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Status Update Section */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-700">Update Order Status</h3>
                  </div>
                  <div className="p-4">
                    <Select 
                      value={selectedOrder.status} 
                      onValueChange={(value) => handleUpdateStatus(selectedOrder.id, value)}
                    >
                      <SelectTrigger data-testid="update-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="payment_pending">Payment Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="refunded">Refunded</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Close Button */}
                <div className="pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDetailDialog(false)}
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
